import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";

import { clearMfaFlow } from "app/store/modules/actions/auth.actions";

const DEFER_DURATION_MS = 3 * 24 * 60 * 60 * 1000;

const dismissalKey = (userId, cutoverAt) => `mfa-recommendation-dismissed:${userId}:${cutoverAt}`;

export default function RecommendedMfaSetupDialog() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const flow = useSelector((state) => state.auth.mfaFlow);
  const isOnSetupScreen = location.pathname === "/auth/two-factor-setup";
  const isRecommendedSetup = flow?.kind === "login" && Boolean(flow.setupToken);
  const cutoverAt = new Date(flow?.mfaCutoverAt).getTime();
  const canDefer = Number.isFinite(cutoverAt) && Date.now() < cutoverAt;
  const deferredUntil = canDefer
    ? Number(localStorage.getItem(dismissalKey(flow?.userId, flow?.mfaCutoverAt)))
    : 0;
  const isDeferred = canDefer && deferredUntil > Date.now();

  useEffect(() => {
    if (isRecommendedSetup && isDeferred) dispatch(clearMfaFlow());
  }, [dispatch, isDeferred, isRecommendedSetup]);

  if (!isRecommendedSetup || isOnSetupScreen || isDeferred) return null;

  const handleDismiss = () => {
    if (!canDefer) return;
    localStorage.setItem(
      dismissalKey(flow.userId, flow.mfaCutoverAt),
      String(Date.now() + DEFER_DURATION_MS)
    );
    dispatch(clearMfaFlow());
  };
  const handleStartSetup = () => navigate("/auth/two-factor-setup");

  return (
    <Dialog
      open
      onClose={canDefer ? handleDismiss : undefined}
      aria-labelledby="recommended-mfa-setup-title"
    >
      <DialogTitle id="recommended-mfa-setup-title" className="f-20px text-dark p-30px pb-0">
        Set up two-factor authentication
      </DialogTitle>
      <DialogContent className="f-14px text-dark fw-300 pt-20px pb-0">
        Two-factor authentication is not required yet, but setting it up now will keep your account
        ready for the upcoming security change.
      </DialogContent>
      <DialogActions className="p-30px">
        {canDefer && (
          <button
            type="button"
            className="btn btn-secondary btn-elevate py-3"
            onClick={handleDismiss}
          >
            Not now
          </button>
        )}
        <button
          type="button"
          className="btn btn-primary btn-elevate py-3 ml-2"
          onClick={handleStartSetup}
        >
          Set up 2FA
        </button>
      </DialogActions>
    </Dialog>
  );
}
